import { Plus, Trash } from "@medusajs/icons"
import { Button, IconButton, Input, Text } from "@medusajs/ui"
import { useState } from "react"
import { Link } from "react-router-dom"
import { DataTable, EmptyState, SearchInput, Section, Status, Thumb } from "@/components/ui"
import { FIELD_LABELS, PRICE_OP_LABELS, TEXT_OP_LABELS, useAutomatedMembers, usePreviewRules, type Rule, type Rules } from "@/lib/collections"
import { Banner } from "@/terabizz"

const FIELDS = Object.keys(FIELD_LABELS) as Rule["field"][]
const blank = (): Rule => ({ field: "tag", op: "equals", value: "" })

/** "Collection type" card: manual (pick products) or automated (products that meet conditions). */
export function CollectionType({ value, onChange, disabled }: { value: Rules | null; onChange: (r: Rules | null) => void; disabled?: boolean }) {
  const auto = !!value
  const preview = usePreviewRules(value)
  const setRule = (i: number, patch: Partial<Rule>) => {
    if (!value) return
    const rules = value.rules.map((r, j) => {
      if (j !== i) return r
      const next = { ...r, ...patch }
      // switching to/from price resets the operator to one that exists for the field
      if (patch.field && (patch.field === "price") !== (r.field === "price")) return { ...next, op: "equals", value: "" }
      return next
    })
    onChange({ ...value, rules })
  }
  return (
    <Section title="Collection type">
      <div className="flex flex-col gap-y-3 px-4 py-4">
        {(
          [
            [false, "Manual", "Add products one by one."],
            [true, "Automated", "Existing and future products that match the conditions are added automatically."],
          ] as const
        ).map(([isAuto, label, hint]) => (
          <label key={label} className="flex cursor-pointer items-start gap-x-2.5">
            <input type="radio" name="collection-type" className="mt-1" checked={auto === isAuto} disabled={disabled} onChange={() => onChange(isAuto ? { match: "all", rules: [blank()] } : null)} />
            <span>
              <span className="block text-[13px] font-medium">{label}</span>
              <span className="block text-[12px] text-[var(--tb-ink-3)]">{hint}</span>
            </span>
          </label>
        ))}

        {value && (
          <fieldset disabled={disabled} className="mt-1 flex flex-col gap-y-3 border-t border-[var(--tb-line)] pt-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
              <span className="font-medium">Products must match:</span>
              {(["all", "any"] as const).map((m) => (
                <label key={m} className="flex items-center gap-x-1.5">
                  <input type="radio" name="match" checked={value.match === m} onChange={() => onChange({ ...value, match: m })} /> {m === "all" ? "all conditions" : "any condition"}
                </label>
              ))}
            </div>
            {value.rules.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <select aria-label="Field" value={r.field} onChange={(e) => setRule(i, { field: e.target.value as Rule["field"] })} className="w-full sm:w-40">
                  {FIELDS.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                </select>
                <select aria-label="Condition" value={r.op} onChange={(e) => setRule(i, { op: e.target.value })} className="w-full sm:w-44">
                  {(r.field === "price" ? PRICE_OP_LABELS : TEXT_OP_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Input aria-label="Value" className="min-w-0 flex-1" inputMode={r.field === "price" ? "decimal" : undefined} placeholder={r.field === "price" ? "19.99" : "Value"} maxLength={100} value={r.value} onChange={(e) => setRule(i, { value: e.target.value })} />
                <IconButton type="button" variant="transparent" size="small" aria-label="Remove condition" disabled={value.rules.length === 1} onClick={() => onChange({ ...value, rules: value.rules.filter((_, j) => j !== i) })}>
                  <Trash />
                </IconButton>
              </div>
            ))}
            <div>
              <Button type="button" size="small" variant="secondary" disabled={value.rules.length >= 10} onClick={() => onChange({ ...value, rules: [...value.rules, blank()] })}>
                <Plus /> Add another condition
              </Button>
            </div>

            <div aria-live="polite">
              {preview.data ? (
                <Banner tone="info" title={`${preview.data.count} ${preview.data.count === 1 ? "product matches" : "products match"} right now`}>
                  {preview.data.products.length ? (
                    <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                      {preview.data.products.slice(0, 6).map((p) => <li key={p.id} className="flex items-center gap-x-1.5"><Thumb src={p.thumbnail} size={20} />{p.title}</li>)}
                    </ul>
                  ) : "Nothing yet. Check the conditions."}
                </Banner>
              ) : preview.error ? (
                <Banner tone="critical">Couldn't check the conditions. {(preview.error as any)?.message}</Banner>
              ) : (
                <Text size="xsmall" className="text-[var(--tb-ink-3)]">Fill in every condition to see the matching products.</Text>
              )}
            </div>
          </fieldset>
        )}
      </div>
    </Section>
  )
}

/** Read-only member list for a saved automated collection. */
export function AutomatedMembers({ id }: { id: string }) {
  const [q, setQ] = useState("")
  const [page, setPage] = useState(0)
  const { data, isLoading } = useAutomatedMembers(id, { q, limit: 10, offset: page * 10 })
  return (
    <Section title="Products in this collection">
      <div className="px-4 py-3">
        <SearchInput value={q} onChange={(v) => { setQ(v); setPage(0) }} placeholder="Search matching products" />
      </div>
      <DataTable
        rowKey={(p: any) => p.id}
        rows={data?.products}
        count={data?.count ?? 0}
        pageSize={10}
        pageIndex={page}
        onPageChange={setPage}
        loading={isLoading}
        empty={<EmptyState title="No products match yet" body="Products are added here automatically when they meet the conditions." />}
        columns={[
          { key: "title", header: "Product", cell: (p: any) => <Link to={`/products/${p.id}`} className="flex items-center gap-x-3"><Thumb src={p.thumbnail} size={36} /><span className="txt-compact-small-plus">{p.title}</span></Link> },
          { key: "status", header: "Status", cell: (p: any) => <Status value={p.status} /> },
        ]}
      />
    </Section>
  )
}
